import React, { useEffect, useState } from 'react';
import { Background, Banner } from './styles';

type Outcome = 'accepted' | 'dismissed';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: Outcome; platform: string }>;
}

const ANDROID_DISMISS_KEY = 'installBannerDismissedAt';
const DAY_MS = 24 * 60 * 60 * 1000;

interface InstallAppProps {
  disabled?: boolean;
}

function isStandalone(): boolean {
  // PWA instalada (Android/iOS/desktop)
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
}

function hasRecentDismissal(): boolean {
  const last = localStorage.getItem(ANDROID_DISMISS_KEY);
  if (!last) return false;
  return Date.now() - Number(last) < DAY_MS;
}

const InstallApp: React.FC<InstallAppProps> = ({ disabled = false }) => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || hasRecentDismissal()) return;

    const onBeforeInstallPrompt = (e: Event) => {
      // Alguns browsers não deixam chamar preventDefault se não for esse tipo
      const ev = e as BeforeInstallPromptEvent;
      ev.preventDefault();
      setDeferred(ev);
      setShow(!disabled);
    };

    const onAppInstalled = () => {
      setShow(false);
      setDeferred(null);
      // opcional: marcar como instalado
      localStorage.removeItem(ANDROID_DISMISS_KEY);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, [disabled]);

  useEffect(() => {
    if (disabled) {
      setShow(false);
      return;
    }

    if (deferred && !isStandalone() && !hasRecentDismissal()) {
      setShow(true);
    }
  }, [disabled, deferred]);

  const handleInstallClick = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      console.log('🧠 Usuário respondeu:', outcome);
      // se recusou, não encher o saco por 24h
      if (outcome === 'dismissed') {
        localStorage.setItem(ANDROID_DISMISS_KEY, String(Date.now()));
      }
    } catch (err) {
      console.warn('Falha ao exibir prompt PWA', err);
    } finally {
      setShow(false);
      setDeferred(null);
    }
  };

  const handleClose = () => {
    localStorage.setItem(ANDROID_DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <Background role="dialog" aria-modal="true" aria-label="Instalar aplicativo">
      <Banner>
        <button className="close" onClick={handleClose} aria-label="Fechar aviso">×</button>
        <div>
          <img
            src="/logo-mini.png"
            alt=""
            width={24}
            height={24}
            style={{ verticalAlign: 'middle', marginRight: 8 }}
          />
          <strong>Instale a Play Moment no seu celular</strong><br />
          Acesso rápido na palma da sua mão.
        </div>
        <button className="install" onClick={handleInstallClick}>
          Instalar agora
        </button>
      </Banner>
    </Background>
  );
};

export default InstallApp;
